import { Box, FormHelperText } from '@material-ui/core'
//@ts-ignore
import NewWindow from 'components/facets/NewWindow.tsx'
import { useUser } from 'components/providers/UserProvider'
import { appToken } from 'config'
import CardmarketLogo from 'img/cardmarket-logo.svg'
import { default as Cookie } from 'js-cookie'
import { recordUsage, switchCardmarketAccount } from 'lib/api'
import { connectCardMarket } from 'lib/auth'
import { useErrorHandler } from 'lib/hooks'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Activity } from 'shared'
import { SalesChannelsConnector } from '../account/SalesChannelConnector'

interface CardmarketConnectProps {
  noHelperText?: boolean
  big?: boolean
  onConnect?: () => void
}

export const useCardmarketConnect = (onConnectSuccess?: () => void) => {
  const { user, fetchUser, setUser } = useUser()

  const { handle } = useErrorHandler()
  const { enqueueSnackbar } = useSnackbar()
  const handleConnectCardmarket = (): void => {
    setShowPopup(true)
  }
  const [showPopup, setShowPopup] = useState(false)
  const [loading, setLoading] = useState(false)
  loading

  const connectCardmarketAccount = useCallback(
    async (cardmarketUsername?: string): Promise<void> => {
      setLoading(true)
      try {
        if (!cardmarketUsername) {
          throw new Error('No cardmarket username provided')
        }
        const response = await switchCardmarketAccount(cardmarketUsername)
        setUser((user) => ({ ...user!, ...response }))
        setLoading(false)
      } catch (e) {
        handleConnectCardmarket()
      }
    },
    [setUser]
  )

  const handleDone = useCallback(async (): Promise<void> => {
    try {
      setShowPopup(false)
      setLoading(true)
      const requestToken = Cookie.get('requestToken')
      if (!requestToken) {
        throw Error(`We couldnt connect your account. If the problem persists, please Contact Us!`)
      }
      if (!process.env.REACT_APP_NODE_ENV) await connectCardMarket(requestToken)
      const wasFirstConnect = !user.assignedCardmarketAccounts.length
      await fetchUser()
      setLoading(false)

      Cookie.remove('requestToken')

      await recordUsage(
        Activity.ConnectCardmarket,
        { name: user.cardmarketUsername },
        wasFirstConnect ? { category: 'conversion', action: 'first_cardmarket_connect' } : undefined
      )
    } catch (error) {
      setLoading(false)
      handle(error)
    }
  }, [setLoading, handle, setUser, fetchUser, user.cardmarketUsername, onConnectSuccess])

  const handleWindowError = useCallback(() => {
    setShowPopup(false)
    setLoading(false)
    enqueueSnackbar(
      'Failed to open Cardmarket authentication window. Please check your popup blocker settings.',
      { variant: 'error' }
    )
  }, [enqueueSnackbar])

  const handleDoneRef = useRef(handleDone)
  const handleWindowErrorRef = useRef(handleWindowError)
  useEffect(() => {
    handleDoneRef.current = handleDone
  }, [handleDone])
  useEffect(() => {
    handleWindowErrorRef.current = handleWindowError
  }, [handleWindowError])

  const Popup = useMemo(
    () => () =>
      showPopup ? (
        <NewWindow
          url={'https://apiv2.cardmarket.com/ws/v2.0/authenticate/' + appToken}
          onUnload={() => handleDoneRef.current()}
          onBlock={() => handleWindowErrorRef.current()}
        />
      ) : (
        <></>
      ),
    [showPopup]
  )

  return [connectCardmarketAccount, Popup, loading || showPopup] as const
}

export const CardmarketConnect = ({
  noHelperText = false,
  big = false,
  onConnect,
}: CardmarketConnectProps): JSX.Element => {
  const { user } = useUser()

  const { t } = useTranslation()
  const [connectCardmarketAccount, Popup] = useCardmarketConnect()

  if (!user) return <></>

  return (
    <div>
      {user.cardmarketUsername ? (
        onConnect ? (
          onConnect()
        ) : (
          <>
            <div className="d-flex flex-wrap mt-2">
              {user.assignedCardmarketAccounts
                .sort((a, b) => a.username.localeCompare(b.username))
                .map((account) => {
                  return (
                    <div className="me-4" key={account.username}>
                      <SalesChannelsConnector
                        type="Cardmarket"
                        onBoxClick={() => connectCardmarketAccount(account.username)}
                        title={account.username}
                        imgIcon={CardmarketLogo}
                        active={account.username === user.cardmarketUsername}
                        buttons={[]}
                      />
                    </div>
                  )
                })}
            </div>
          </>
        )
      ) : (
        <div>
          <Box textAlign={big ? 'center' : 'initial'}>
            <div className="my-3">
              <SalesChannelsConnector
                type="Cardmarket"
                onBoxClick={() => connectCardmarketAccount()}
                title={t('connectCardmarket.connectButton')}
                imgIcon={CardmarketLogo}
                active={false}
                buttons={[
                  [
                    <span key="currentlyActive">{t('connectCardmarket.currentlyActive')}</span>,
                    () => {
                      return
                    },
                  ],
                ]}
              />
            </div>
            {!noHelperText && (
              <div className="d-flex flex-column">
                <FormHelperText>{t('connectCardmarket.helperText1')}</FormHelperText> &nbsp;
                <FormHelperText>{t('connectCardmarket.helperText2')}</FormHelperText>
              </div>
            )}
          </Box>
        </div>
      )}
      <Popup />
    </div>
  )
}
